import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSeason } from '../contexts/SeasonContext';
import { apiRequest } from '../config/api';
import { PlayerStats, LeagueDay, PlayerHistory, Division, Badge, BadgeType } from '../types';
import './SplLanding.css';

// ─── Types ────────────────────────────────────────────────────────────────

type Lang = 'en' | 'de';
type SortKey = 'rank' | 'points' | 'played' | 'wins' | 'losses';
type SortDir = 'asc' | 'desc';

interface DivConfig {
  id: Division;
  label: string;
  shortLabel: string;
  tier: string;
  kind: 'open' | 'women';
}

interface RecentResult {
  win: boolean;
  opponentName: string;
  score: string;
  date: string;
}

interface H2HEntry {
  name: string;
  rank: number;
  wins: number;
  losses: number;
}

interface ModalPlayer {
  stats: PlayerStats;
  rank: number;
  divLabel: string;
  seasonName: string;
}

// ─── Constants ────────────────────────────────────────────────────────────

const SPL_DIVISIONS: DivConfig[] = [
  { id: 'Master',          label: 'Master Division', shortLabel: 'Master',    tier: '◆',  kind: 'open'  },
  { id: 'Division 1',      label: 'Division 1',       shortLabel: 'Div. 1',   tier: '01', kind: 'open'  },
  { id: 'Division 2A',     label: 'Division 2A',      shortLabel: 'Div. 2A',  tier: '02', kind: 'open'  },
  { id: 'Division 2B',     label: 'Division 2B',      shortLabel: 'Div. 2B',  tier: '02', kind: 'open'  },
  { id: 'Division 3',      label: 'Division 3',       shortLabel: 'Div. 3',   tier: '03', kind: 'open'  },
  { id: 'Division 4',      label: 'Division 4',       shortLabel: 'Div. 4',   tier: '04', kind: 'open'  },
  { id: 'Women Division',  label: 'Women Div. 1',     shortLabel: 'Women 1',  tier: '◆',  kind: 'women' },
  { id: 'Women Division 2',label: 'Women Div. 2',     shortLabel: 'Women 2',  tier: '01', kind: 'women' },
];

// Divisions shown in the Season Snapshot section
const SNAPSHOT_DIVS: Division[] = ['Master', 'Division 1', 'Division 2A', 'Women Division'];

const PLACEHOLDER_NEWS = [
  { tag: 'Season', date: 'May 2026',    title: 'Spring 2026 season reaches the halfway point — Master Division still wide open', featured: true },
  { tag: 'Format', date: 'Apr 2026',    title: 'New Division 2B added for Autumn 2026 to accommodate growing player base', featured: false },
  { tag: 'Results', date: 'Apr 2026',   title: 'Round 9 recap: dramatic matches across all eight divisions', featured: false },
];

const PLACEHOLDER_SPONSORS = ['SPL Partner', 'SportZone', 'PadelPro', 'Stuttgart Sport', 'Court & Racket'];

// ─── Translations ─────────────────────────────────────────────────────────

const T = {
  en: {
    nav: { rankings: 'Rankings', divisions: 'Divisions', matches: 'Matches', about: 'About', join: 'Join' },
    hero: {
      title_a: 'Stuttgart', title_b: 'Padel', title_c: 'League',
      lede: 'Eight divisions. Twelve weeks. One ranking that decides who plays where next season.',
      cta1: 'View Rankings', cta2: 'Register',
    },
    heroStats: [
      { v: 'Active Players' }, { k: '8', v: 'Divisions' },
      { v: 'Matches / Season' }, { k: '5', v: 'Partner Clubs' },
    ],
    rankings: {
      title: 'Live Rankings', sub: 'Updated continuously throughout match weekends.',
      search: 'Search players…', season: 'Season',
      empty: 'No players match your search.', viewAll: 'View full ranking →',
      hideGuests: 'Hide guests',
      h: { rank: '#', player: 'Player', club: 'Club', played: 'MP', wins: 'W', losses: 'L', points: 'PTS', form: 'Form' },
    },
    divisions: {
      title: 'Divisions', sub: 'A pyramid that rewards consistency. Promotion and relegation between adjacent divisions happens every season.',
      players: 'players', open: 'Open', women: 'Women',
    },
    matches: {
      title: 'Upcoming', sub: 'Upcoming and active league days across all divisions.',
      live: 'LIVE', today: 'TODAY', upcoming: 'UPCOMING',
    },
    standings: {
      title: 'Season Snapshot', sub: 'Where each division stands.',
      promotion: 'Promotion', relegation: 'Relegation', gapTo2: '+{n} pts to 2nd',
    },
    results: { title: 'Recent Results', sub: 'Confirmed results from recent sessions.', def: 'def.' },
    news: { title: 'League News', sub: 'Reports, format changes and announcements.' },
    about: {
      title: 'A league, not a tournament.',
      lede: 'SPL runs three seasons a year across greater Stuttgart. We borrow the structure of professional football — a tiered pyramid with promotion and relegation — and apply it to the fastest-growing racquet sport in Europe.',
      facts: [{ k: '2019', v: 'Founded' }, { k: '8', v: 'Divisions' }, { k: '5', v: 'Partner Clubs' }, { k: '18', v: 'Courts' }],
    },
    join: {
      title_a: 'Join the', title_b: 'next season.',
      lede: 'Registration for next season is open. Single players and pairs welcome.',
      step1: 'Create your player profile', step2: 'Self-rate or import your ranking', step3: 'Pay the seasonal fee · €145',
      cta: 'Register Now', altCta: 'Notify me when registration opens', placeholder: 'your@email.de',
    },
    sponsors: { title: 'Partners' },
    faq: {
      title: 'Frequently Asked',
      items: [
        { q: 'How does promotion and relegation work?', a: 'After each season, the top 2 players in each division are promoted to the division above, and the bottom 2 are relegated. The Master Division has no promotion.' },
        { q: 'How many league days are in a season?', a: 'Each season runs approximately 10–14 league days, held on weekends at partner clubs across Stuttgart.' },
        { q: 'Can I play with a partner?', a: 'Yes. Players can register and play with a preferred partner, though partners are not fixed — the format encourages playing with different opponents each round.' },
        { q: 'What is the season fee?', a: 'The seasonal fee is €145, which covers all league days in the season. A one-time registration fee applies for new players.' },
        { q: 'How is the ranking calculated?', a: 'The ranking combines match win rate with participation frequency using a Bayesian scoring system, ensuring consistent participants are rewarded alongside high win rates.' },
      ],
    },
    footer: {
      tagline: 'Padel, structured.',
      rights: '© 2026 Stuttgart Padel League e.V.',
      links: ['Imprint', 'Privacy', 'Code of Conduct'],
    },
    profile: {
      points: 'Points', record: 'Win — Loss', winRate: 'Win Rate', leagueDays: 'League Days',
      history: 'Points across season', results: 'Recent Match Results',
      meta: 'Profile', h2h: 'Recent Opponents',
      rating: 'SPL Rating', handedness: 'Handedness', side: 'Preferred Side', streak: 'Streak', sets: 'Sets',
    },
    season: 'Season', round: 'Round', of: 'of',
    loading: 'LOADING',
  },
  de: {
    nav: { rankings: 'Rangliste', divisions: 'Divisionen', matches: 'Spiele', about: 'Über', join: 'Mitspielen' },
    hero: {
      title_a: 'Stuttgart', title_b: 'Padel', title_c: 'Liga',
      lede: 'Acht Divisionen. Zwölf Wochen. Eine Rangliste, die entscheidet, wer nächste Saison wo spielt.',
      cta1: 'Rangliste ansehen', cta2: 'Anmelden',
    },
    heroStats: [
      { v: 'Aktive Spieler' }, { k: '8', v: 'Divisionen' },
      { v: 'Spiele / Saison' }, { k: '5', v: 'Partnerclubs' },
    ],
    rankings: {
      title: 'Live-Rangliste', sub: 'Wird an Spielwochenenden laufend aktualisiert.',
      search: 'Spieler suchen…', season: 'Saison',
      empty: 'Keine Spieler gefunden.', viewAll: 'Vollständige Rangliste →',
      hideGuests: 'Gäste ausblenden',
      h: { rank: '#', player: 'Spieler', club: 'Club', played: 'SP', wins: 'S', losses: 'N', points: 'PKT', form: 'Form' },
    },
    divisions: {
      title: 'Divisionen', sub: 'Eine Pyramide, die Konstanz belohnt. Auf- und Abstieg zwischen den Divisionen erfolgt jede Saison.',
      players: 'Spieler', open: 'Offen', women: 'Frauen',
    },
    matches: {
      title: 'Anstehend', sub: 'Anstehende und aktive Spieltage in allen Divisionen.',
      live: 'LIVE', today: 'HEUTE', upcoming: 'KOMMEND',
    },
    standings: {
      title: 'Saison-Übersicht', sub: 'Stand der Divisionen.',
      promotion: 'Aufstieg', relegation: 'Abstieg', gapTo2: '+{n} Pkt. zu Platz 2',
    },
    results: { title: 'Letzte Ergebnisse', sub: 'Bestätigte Ergebnisse der letzten Spieltage.', def: 'besiegt' },
    news: { title: 'Liga-News', sub: 'Berichte, Formatänderungen und Ankündigungen.' },
    about: {
      title: 'Eine Liga, kein Turnier.',
      lede: 'Die SPL trägt drei Saisons pro Jahr im Großraum Stuttgart aus. Wir übernehmen die Struktur des Profifußballs — eine Pyramide mit Auf- und Abstieg — und wenden sie auf die am schnellsten wachsende Schlägersportart Europas an.',
      facts: [{ k: '2019', v: 'Gegründet' }, { k: '8', v: 'Divisionen' }, { k: '5', v: 'Partnerclubs' }, { k: '18', v: 'Plätze' }],
    },
    join: {
      title_a: 'Sei', title_b: 'nächste Saison dabei.',
      lede: 'Die Anmeldung für die nächste Saison ist offen. Einzelspieler und Paare willkommen.',
      step1: 'Spielerprofil anlegen', step2: 'Selbst einschätzen oder Ranking importieren', step3: 'Saisongebühr · 145 €',
      cta: 'Jetzt anmelden', altCta: 'Benachrichtige mich zur Anmeldung', placeholder: 'deine@email.de',
    },
    sponsors: { title: 'Partner' },
    faq: {
      title: 'Häufige Fragen',
      items: [
        { q: 'Wie funktioniert Auf- und Abstieg?', a: 'Nach jeder Saison steigen die besten 2 Spieler jeder Division auf, und die letzten 2 steigen ab. Die Master Division hat keinen Aufstieg.' },
        { q: 'Wie viele Spieltage gibt es pro Saison?', a: 'Jede Saison umfasst ca. 10–14 Spieltage an Wochenenden in Partnervereinen rund um Stuttgart.' },
        { q: 'Kann ich mit einem Partner spielen?', a: 'Ja. Spieler können sich mit einem bevorzugten Partner anmelden, obwohl Partner nicht für alle Runden fest sind.' },
        { q: 'Wie hoch ist die Saisongebühr?', a: 'Die Saisongebühr beträgt 145 €, die alle Spieltage der Saison abdeckt. Für neue Spieler gilt einmalig eine Anmeldegebühr.' },
        { q: 'Wie wird die Rangliste berechnet?', a: 'Die Rangliste kombiniert Siegquote und Teilnahmehäufigkeit mit einem Bayesischen Scoring-System, das sowohl häufige Teilnahme als auch hohe Siegquoten belohnt.' },
      ],
    },
    footer: {
      tagline: 'Padel, strukturiert.',
      rights: '© 2026 Stuttgart Padel League e.V.',
      links: ['Impressum', 'Datenschutz', 'Verhaltenskodex'],
    },
    profile: {
      points: 'Punkte', record: 'Siege — Niederlagen', winRate: 'Siegquote', leagueDays: 'Spieltage',
      history: 'Punkte im Saisonverlauf', results: 'Letzte Spielergebnisse',
      meta: 'Profil', h2h: 'Letzte Gegner',
      rating: 'SPL-Wertung', handedness: 'Schlaghand', side: 'Bevorzugte Seite', streak: 'Serie', sets: 'Sätze',
    },
    season: 'Saison', round: 'Runde', of: 'von',
    loading: 'LADEN',
  },
} as const;

// ─── Utilities ────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function toJsDate(d: any): Date {
  if (!d) return new Date(NaN);
  if (d instanceof Date) return d;
  if (typeof d === 'string' || typeof d === 'number') return new Date(d);
  const secs = d._seconds ?? d.seconds;
  if (typeof secs === 'number') return new Date(secs * 1000);
  return new Date(NaN);
}

function fmtDate(d: Date, lang: Lang): string {
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtShortDate(d: Date, lang: Lang): string {
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'short' });
}

function getLeagueDayStatus(ld: LeagueDay): 'live' | 'today' | 'upcoming' {
  const now = new Date();
  const ldDate = toJsDate(ld.date);
  const isToday =
    ldDate.getFullYear() === now.getFullYear() &&
    ldDate.getMonth() === now.getMonth() &&
    ldDate.getDate() === now.getDate();
  if (isToday && !ld.isCompleted) return ld.currentRound > 0 ? 'live' : 'today';
  return 'upcoming';
}

function getFormBars(matchesWon: number, matchesLost: number): ('w' | 'l' | 'n')[] {
  const total = matchesWon + matchesLost;
  if (total === 0) return ['n', 'n', 'n', 'n', 'n'];
  const winRate = matchesWon / total;
  // Distribute 5 bars weighted by win rate using golden-ratio spread
  return Array.from({ length: 5 }, (_, i) => {
    const phase = (i * 0.618034 + 0.1) % 1;
    return phase < winRate ? 'w' : 'l';
  });
}

function buildPointsHistory(leagueDayStats: any[]): number[] {
  if (!leagueDayStats || leagueDayStats.length === 0) return [];
  const sorted = [...leagueDayStats]
    .filter(s => s.participated !== false)
    .sort((a, b) => (a.dayNumber ?? 0) - (b.dayNumber ?? 0));
  // Cumulative points
  let cum = 0;
  return sorted.map(s => { cum += (s.points ?? 0); return cum; });
}

function deriveRecentResults(history: PlayerHistory, playerId: string): RecentResult[] {
  if (!history?.matches?.length) return [];
  const sorted = [...history.matches]
    .filter(m => m.isCompleted)
    .sort((a, b) => {
      const da = new Date(a.scoredAt as any || a.createdAt as any || 0).getTime();
      const db = new Date(b.scoredAt as any || b.createdAt as any || 0).getTime();
      return db - da;
    })
    .slice(0, 5);

  return sorted.map(m => {
    const isPair1 = m.pair1.player1Id === playerId || m.pair1.player2Id === playerId;
    const isWin = isPair1 ? m.score.winnerPair === 1 : m.score.winnerPair === 2;
    const g1 = m.score.pair1Games;
    const g2 = m.score.pair2Games;
    const scoreStr = isPair1 ? `${g1}—${g2}` : `${g2}—${g1}`;
    const opp = isPair1
      ? `${m.pair2Player1Name} / ${m.pair2Player2Name}`
      : `${m.pair1Player1Name} / ${m.pair1Player2Name}`;
    return {
      win: isWin,
      opponentName: opp,
      score: scoreStr,
      date: fmtShortDate(toJsDate(m.scoredAt as any || m.createdAt as any || null), 'en'),
    };
  });
}

function deriveH2H(history: PlayerHistory, playerId: string): H2HEntry[] {
  if (!history?.matches?.length) return [];
  const opponentMap = new Map<string, { name: string; wins: number; losses: number }>();
  for (const m of history.matches) {
    if (!m.isCompleted) continue;
    const isPair1 = m.pair1.player1Id === playerId || m.pair1.player2Id === playerId;
    const isWin = isPair1 ? m.score.winnerPair === 1 : m.score.winnerPair === 2;
    const oppIds = isPair1
      ? [m.pair2.player1Id, m.pair2.player2Id]
      : [m.pair1.player1Id, m.pair1.player2Id];
    const oppNames = isPair1
      ? [m.pair2Player1Name, m.pair2Player2Name]
      : [m.pair1Player1Name, m.pair1Player2Name];

    for (let i = 0; i < oppIds.length; i++) {
      const id = oppIds[i];
      const name = oppNames[i];
      if (!id || id === playerId) continue;
      const existing = opponentMap.get(id) || { name, wins: 0, losses: 0 };
      if (isWin) existing.wins++;
      else existing.losses++;
      opponentMap.set(id, existing);
    }
  }
  return Array.from(opponentMap.entries())
    .map(([, v]) => ({ name: v.name, rank: 0, wins: v.wins, losses: v.losses }))
    .sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses))
    .slice(0, 5);
}

// ─── SVG Components ───────────────────────────────────────────────────────

function CourtSvg() {
  // Rally path: B(top-right) → D(bottom-right) → A(top-left) → C(bottom-left) → B
  // Down-the-line shots arc outward; cross-court shots arc through the middle
  const rallyPath = "M 132 44 C 145 90 145 140 132 176 C 100 160 80 95 68 44 C 55 90 55 140 68 176 C 100 160 122 95 132 44";
  const ease = ".42 0 .58 1";
  const splines = `${ease}; ${ease}; ${ease}; ${ease}`;

  return (
    <svg viewBox="0 0 200 220" className="court-svg" preserveAspectRatio="xMidYMid meet">
      <defs>
        <path id="spl-court-rally" d={rallyPath} />
      </defs>

      {/* Court outline */}
      <rect x="20" y="20" width="160" height="180" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.9" />
      {/* Service lines */}
      <line x1="20" y1="68"  x2="180" y2="68"  stroke="var(--accent)" strokeWidth="0.6" opacity="0.65" />
      <line x1="20" y1="152" x2="180" y2="152" stroke="var(--accent)" strokeWidth="0.6" opacity="0.65" />
      {/* Center line */}
      <line x1="100" y1="20" x2="100" y2="200" stroke="var(--accent)" strokeWidth="0.4" opacity="0.45" />
      {/* Net */}
      <line x1="20" y1="110" x2="180" y2="110" stroke="var(--accent)" strokeWidth="2.5" opacity="1" />
      <line x1="20" y1="107" x2="180" y2="107" stroke="var(--accent)" strokeWidth="0.3" strokeDasharray="2 3" opacity="0.35" />
      {/* Corner markers */}
      <circle cx="20"  cy="20"  r="2.5" fill="var(--accent)" />
      <circle cx="180" cy="20"  r="2.5" fill="var(--accent)" />
      <circle cx="20"  cy="200" r="2.5" fill="var(--accent)" />
      <circle cx="180" cy="200" r="2.5" fill="var(--accent)" />

      {/* Players — all 4 animate with a hit-flash synced to the ball */}
      {/* A: top-left — hits at t=2s (delay=-2s) */}
      <circle cx="68"  cy="44"  r="5" fill="var(--text)" className="court-player court-player-a" />
      {/* B: top-right — hits at t=0s (delay=0s) */}
      <circle cx="132" cy="44"  r="5" fill="var(--text)" className="court-player court-player-b" />
      {/* C: bottom-left — hits at t=3s (delay=-1s) */}
      <circle cx="68"  cy="176" r="5" fill="var(--text)" className="court-player court-player-c" />
      {/* D: bottom-right — hits at t=1s (delay=-3s) */}
      <circle cx="132" cy="176" r="5" fill="var(--text)" className="court-player court-player-d" />

      {/* Ball glow */}
      <circle r="10" fill="var(--accent)" opacity="0.15">
        <animateMotion dur="4s" repeatCount="indefinite"
          calcMode="spline"
          keyPoints="0; 0.25; 0.5; 0.75; 1"
          keyTimes="0; 0.25; 0.5; 0.75; 1"
          keySplines={splines}>
          <mpath href="#spl-court-rally" />
        </animateMotion>
      </circle>
      {/* Ball */}
      <circle r="3.5" fill="var(--accent-bright)" className="court-ball">
        <animateMotion dur="4s" repeatCount="indefinite"
          calcMode="spline"
          keyPoints="0; 0.25; 0.5; 0.75; 1"
          keyTimes="0; 0.25; 0.5; 0.75; 1"
          keySplines={splines}>
          <mpath href="#spl-court-rally" />
        </animateMotion>
      </circle>
    </svg>
  );
}

function NewsPlaceholderSvg() {
  return (
    <svg className="news-placeholder" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="diag" patternUnits="userSpaceOnUse" width="12" height="12" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="12" stroke="var(--border)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#diag)" />
    </svg>
  );
}

function PointsChart({ points }: { points: number[] }) {
  if (!points.length) return (
    <div className="spl-chart-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)' }}>No data</span>
    </div>
  );
  const W = 600, H = 130, pad = 10;
  const maxP = Math.max(...points, 1);
  const minP = Math.min(...points, 0);
  const yRange = Math.max(1, maxP - minP);
  const xStep = points.length > 1 ? (W - pad * 2) / (points.length - 1) : W - pad * 2;
  const pts = points.map((p, i) => ({
    x: pad + i * xStep,
    y: H - pad - ((p - minP) / yRange) * (H - pad * 2),
  }));
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${pts[pts.length - 1].x} ${H - pad} L ${pts[0].x} ${H - pad} Z`;
  const gridYs = [0.25, 0.5, 0.75].map(f => pad + (H - pad * 2) * f);
  return (
    <div className="spl-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="cg" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.32" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridYs.map(y => (
          <line key={y} x1={pad} x2={W - pad} y1={y} y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="3 5" />
        ))}
        <path d={areaPath} fill="url(#cg)" />
        <path d={linePath} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y}
            r={i === pts.length - 1 ? 4.5 : 2.5}
            fill="var(--accent)" stroke="var(--bg)" strokeWidth="1.5" />
        ))}
      </svg>
    </div>
  );
}

// ─── Player Modal ─────────────────────────────────────────────────────────

const BADGE_INFO: Record<BadgeType, { name: string; emoji: string }> = {
  first_win:          { name: 'First Victory',      emoji: '🎉' },
  giant_killer:       { name: 'Giant Killer',        emoji: '⚔️' },
  win_streak_10:      { name: 'Unstoppable',         emoji: '🔥' },
  division_champion:  { name: 'Division Champion',   emoji: '👑' },
  close_call:         { name: 'Close Call',          emoji: '💪' },
  perfect_day:        { name: 'Perfect Day',         emoji: '⭐' },
  veteran:            { name: 'Veteran',             emoji: '🎖️' },
  partner_carry:      { name: 'Partner Carry',       emoji: '🤝' },
  clean_sweep:        { name: 'Clean Sweep',         emoji: '🧹' },
  mr_ms_consistent:   { name: 'Mr/Ms Consistent',   emoji: '📊' },
  century:            { name: 'Century',             emoji: '💯' },
  iron_man:           { name: 'Iron Man',            emoji: '🦾' },
  all_season_warrior: { name: 'All-Season Warrior',  emoji: '🛡️' },
  division_hopper:    { name: 'Division Hopper',     emoji: '🦘' },
  unlucky:            { name: 'Unlucky',             emoji: '😤' },
  sp_team:            { name: 'SP Team',             emoji: '⚡' },
};

interface PlayerModalProps {
  player: ModalPlayer;
  onClose: () => void;
  t: typeof T['en'];
  lang: Lang;
  seasonId: string | null;
}

function PlayerModal({ player, onClose, t, lang, seasonId }: PlayerModalProps) {
  const { stats } = player;
  const [history, setHistory] = useState<PlayerHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    setHistory(null);
    setExpandedDates(new Set());
    const params = new URLSearchParams({ division: stats.division });
    if (seasonId) params.append('seasonId', seasonId);
    apiRequest(`/api/public/players/${stats.playerId}/history?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setHistory(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [stats.playerId, stats.division, seasonId]);

  const totalMatches = stats.matchesWon + stats.matchesLost;
  const winRate = totalMatches > 0 ? Math.round((stats.matchesWon / totalMatches) * 100) : 0;
  const initials = getInitials(stats.playerName);
  const nameParts = stats.playerName.trim().split(/\s+/);
  const firstName = nameParts.slice(0, -1).join(' ');
  const lastName = nameParts[nameParts.length - 1];

  const pointsHistory = history ? buildPointsHistory(history.leagueDayStats) : [];
  const recentResults = history ? deriveRecentResults(history, stats.playerId) : [];
  const h2hEntries = history ? deriveH2H(history, stats.playerId) : [];

  const p = stats as any;
  const rating = p.rating ?? p.level ?? '—';
  const handedness = p.handedness
    ? (p.handedness === 'L' ? (lang === 'de' ? 'Links' : 'Left') : (lang === 'de' ? 'Rechts' : 'Right'))
    : '—';
  const side = p.preferredSide ?? p.side ?? '—';
  const streak = p.streak ?? `W${stats.matchesWon > stats.matchesLost ? Math.abs(stats.matchesWon - stats.matchesLost) : 0}`;

  const xLabels: string[] = [];
  if (pointsHistory.length > 0) {
    const step = Math.max(1, Math.ceil(pointsHistory.length / 6));
    for (let i = 0; i < pointsHistory.length; i += step) xLabels.push(`R${i + 1}`);
  }

  // Partnerships & opponents computed from full match history
  const partnerships = useMemo(() => {
    if (!history?.matches) return [];
    const map: Record<string, { name: string; won: number; total: number }> = {};
    history.matches.forEach(m => {
      const ip1 = m.pair1.player1Id === stats.playerId || m.pair1.player2Id === stats.playerId;
      const pid = ip1
        ? (m.pair1.player1Id === stats.playerId ? m.pair1.player2Id : m.pair1.player1Id)
        : (m.pair2.player1Id === stats.playerId ? m.pair2.player2Id : m.pair2.player1Id);
      const pname = ip1
        ? (m.pair1.player1Id === stats.playerId ? m.pair1Player2Name : m.pair1Player1Name)
        : (m.pair2.player1Id === stats.playerId ? m.pair2Player2Name : m.pair2Player1Name);
      if (!pid) return;
      if (!map[pid]) map[pid] = { name: pname, won: 0, total: 0 };
      map[pid].total++;
      if (ip1 ? m.score.winnerPair === 1 : m.score.winnerPair === 2) map[pid].won++;
    });
    return Object.values(map).map(s => ({ name: s.name, won: s.won, played: s.total, rate: s.total > 0 ? (s.won / s.total) * 100 : 0 })).sort((a, b) => b.won - a.won);
  }, [history, stats.playerId]);

  const opponents = useMemo(() => {
    if (!history?.matches) return [];
    const map: Record<string, { name: string; lost: number; total: number }> = {};
    history.matches.forEach(m => {
      const ip1 = m.pair1.player1Id === stats.playerId || m.pair1.player2Id === stats.playerId;
      const lost = ip1 ? m.score.winnerPair === 2 : m.score.winnerPair === 1;
      const opps = ip1
        ? [{ id: m.pair2.player1Id, name: m.pair2Player1Name }, { id: m.pair2.player2Id, name: m.pair2Player2Name }]
        : [{ id: m.pair1.player1Id, name: m.pair1Player1Name }, { id: m.pair1.player2Id, name: m.pair1Player2Name }];
      opps.forEach(({ id, name }) => {
        if (!id) return;
        if (!map[id]) map[id] = { name, lost: 0, total: 0 };
        map[id].total++;
        if (lost) map[id].lost++;
      });
    });
    return Object.values(map).map(s => ({ name: s.name, lost: s.lost, played: s.total, rate: s.total > 0 ? (s.lost / s.total) * 100 : 0 })).sort((a, b) => b.lost - a.lost);
  }, [history, stats.playerId]);

  // Match history grouped by date
  const matchesByDate = useMemo(() => {
    if (!history?.matches) return [];
    const dateMap = new Map<string, Date>();
    history.allDivisionLeagueDays?.forEach(d => { if (d.id && d.date) dateMap.set(d.id, new Date(d.date)); });
    history.leagueDayStats?.forEach(s => { if (s.leagueDayId && s.date && !dateMap.has(s.leagueDayId)) dateMap.set(s.leagueDayId, toJsDate(s.date)); });
    const sorted = [...history.matches].sort((a, b) => {
      const da = dateMap.get(a.leagueDayId), db = dateMap.get(b.leagueDayId);
      if (da && db) { const diff = db.getTime() - da.getTime(); return diff !== 0 ? diff : (b.roundNumber || 0) - (a.roundNumber || 0); }
      return 0;
    });
    const groups = new Map<string, { date: Date | null; matches: typeof sorted }>();
    sorted.forEach(m => {
      const d = dateMap.get(m.leagueDayId);
      const key = d ? fmtShortDate(d, lang) : 'Unknown';
      if (!groups.has(key)) groups.set(key, { date: d || null, matches: [] });
      groups.get(key)!.matches.push(m);
    });
    return Array.from(groups.entries()).map(([key, v]) => ({ key, ...v }));
  }, [history, lang]);

  const badges = (stats as any).badges as Badge[] | undefined;
  const divStats = history?.divisionStats?.[stats.division];
  const penalties = history?.penalties?.filter(pen => pen.division === stats.division && pen.points !== 0) ?? [];
  const bestPartner = partnerships[0] ?? null;
  const toughestOpp = opponents[0] ?? null;

  const toggleDate = (key: string) => setExpandedDates(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  return (
    <div className="spl-modal-backdrop" onClick={onClose}>
      <div className="spl-modal" onClick={e => e.stopPropagation()}>
        <button className="spl-modal-close" onClick={onClose} aria-label="Close">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <div className="spl-modal-header">
          <div className="spl-av-lg">
            {stats.profilePicture
              ? <img src={stats.profilePicture} alt={stats.playerName} />
              : initials}
          </div>
          <div>
            <div className="spl-modal-eyebrow">
              <span>#{String(player.rank).padStart(2, '0')}</span>
              <span>·</span>
              <span>{player.divLabel}</span>
              <span>·</span>
              <span>{player.seasonName}</span>
            </div>
            <div className="spl-modal-name">
              {firstName && <span>{firstName} </span>}
              <span className="last">{lastName}</span>
            </div>
            <div className="spl-modal-submeta">
              {stats.division && <span className="nat-pill">{stats.division}</span>}
              {stats.nationality && <span className="nat-pill">{stats.nationality}</span>}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="spl-modal-stats">
          <div className="spl-modal-stat">
            <div className="k acc">{stats.totalPoints}</div>
            <div className="v">{t.profile.points}</div>
          </div>
          <div className="spl-modal-stat">
            <div className="k">{stats.matchesWon}<span style={{ color: 'var(--muted)' }}>—</span>{stats.matchesLost}</div>
            <div className="v">{t.profile.record}</div>
          </div>
          <div className="spl-modal-stat">
            <div className="k">{winRate}<span style={{ fontSize: '0.5em', color: 'var(--muted)' }}>%</span></div>
            <div className="v">{t.profile.winRate}</div>
          </div>
          <div className="spl-modal-stat">
            <div className="k">{stats.leagueDaysPlayed}</div>
            <div className="v">{t.profile.leagueDays}</div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="spl-spinner-wrap" style={{ padding: '48px' }}>
            <div className="spl-spinner" />
            <span className="spl-spinner-text">{t.loading}</span>
          </div>
        ) : (
          <>
            <div className="spl-modal-body">
              {/* Left column */}
              <div className="spl-modal-col">
                <div className="spl-modal-section">
                  <h4>{t.profile.history}</h4>
                  <PointsChart points={pointsHistory} />
                  {xLabels.length > 0 && (
                    <div className="chart-x">
                      {xLabels.map((l, i) => <span key={i}>{l}</span>)}
                    </div>
                  )}
                </div>
                <div className="spl-modal-section">
                  <h4>{t.profile.results}</h4>
                  {recentResults.length === 0 ? (
                    <p className="spl-modal-empty">No recent matches.</p>
                  ) : (
                    <div className="spl-recent-results">
                      {recentResults.map((r, i) => (
                        <div key={i} className={`spl-recent-result ${r.win ? 'win' : 'loss'}`}>
                          <span className={`spl-res-badge ${r.win ? 'win' : 'loss'}`}>{r.win ? 'W' : 'L'}</span>
                          <div className="spl-res-opp">
                            <div className="lbl">{r.date}</div>
                            <div className="nm">{r.opponentName}</div>
                          </div>
                          <span className="spl-res-sc">{r.score}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div className="spl-modal-col">
                <div className="spl-modal-section">
                  <h4>{t.profile.meta}</h4>
                  <div className="spl-meta-grid">
                    {[
                      { l: t.profile.rating, v: String(rating), mono: true },
                      { l: t.profile.handedness, v: handedness, mono: false },
                      { l: t.profile.side, v: String(side), mono: false },
                      { l: t.profile.streak, v: streak, mono: true },
                      { l: 'Games W—L', v: `${stats.gamesWon}—${stats.gamesLost}`, mono: true },
                      ...(divStats ? [{ l: 'Pts in division', v: String(divStats.totalPoints), mono: true }] : []),
                    ].map(row => (
                      <div key={row.l} className="spl-meta-row">
                        <span className="l">{row.l}</span>
                        <span className={`v${row.mono ? ' mono' : ''}`}>{row.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="spl-modal-section">
                  <h4>{t.profile.h2h}</h4>
                  {h2hEntries.length === 0 ? (
                    <p className="spl-modal-empty">No data.</p>
                  ) : (
                    h2hEntries.map((opp, i) => (
                      <div key={i} className="spl-h2h-row">
                        <div className="spl-av" style={{ width: 28, height: 28, fontSize: 10 }}>{getInitials(opp.name)}</div>
                        <div className="spl-h2h-info">
                          <div className="spl-h2h-name">{opp.name}</div>
                          <div className="spl-h2h-rank">{stats.division}</div>
                        </div>
                        <span className={`spl-h2h-rec ${opp.wins >= opp.losses ? 'win' : 'loss'}`}>{opp.wins}–{opp.losses}</span>
                      </div>
                    ))
                  )}
                </div>
                {/* Best partnership & toughest opponent */}
                {bestPartner && (
                  <div className="spl-modal-section">
                    <h4>🤝 Best Partnership</h4>
                    <div className="spl-partner-card win">
                      <div className="name">{bestPartner.name}</div>
                      <div className="rec">{bestPartner.won}W — {bestPartner.played - bestPartner.won}L · {bestPartner.rate.toFixed(0)}% win rate</div>
                    </div>
                  </div>
                )}
                {toughestOpp && (
                  <div className="spl-modal-section">
                    <h4>💪 Toughest Opponent</h4>
                    <div className="spl-partner-card loss">
                      <div className="name">{toughestOpp.name}</div>
                      <div className="rec">{toughestOpp.lost} losses in {toughestOpp.played} games · {toughestOpp.rate.toFixed(0)}% loss rate</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Badges */}
            {badges && badges.length > 0 && (() => {
              const grouped: Record<string, { count: number; pts: number; emoji: string; name: string }> = {};
              badges.forEach(b => {
                const info = BADGE_INFO[b.type] ?? { emoji: '🏅', name: b.type };
                if (!grouped[b.type]) grouped[b.type] = { count: 0, pts: 0, emoji: info.emoji, name: info.name };
                grouped[b.type].count++;
                grouped[b.type].pts += b.pointsAwarded ?? 0;
              });
              return (
                <div className="spl-modal-extra">
                  <div className="spl-modal-extra-title">🏆 Achievements</div>
                  <div className="spl-badges-row">
                    {Object.values(grouped).map((g, i) => (
                      <span key={i} className="spl-badge-pill">
                        {g.emoji} {g.name}{g.count > 1 ? ` ×${g.count}` : ''}
                        {g.pts > 0 && <span className="spl-badge-pts">+{g.pts}pts</span>}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Penalties & Bonuses */}
            {penalties.length > 0 && (
              <div className="spl-modal-extra">
                <div className="spl-modal-extra-title">⚖️ Penalties & Bonuses</div>
                {penalties.map((pen, i) => (
                  <div key={i} className={`spl-penalty-row ${pen.type}`}>
                    <div className="spl-penalty-info">
                      <span className="label">{pen.type === 'bonus' ? '✅ Bonus' : '⚠️ Penalty'}</span>
                      <span className="reason">{pen.reason || 'No reason provided'}</span>
                    </div>
                    <span className="pts">{pen.type === 'bonus' ? '+' : '-'}{pen.points}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Other divisions */}
            {history?.divisionStats && (() => {
              const otherDivs = Object.entries(history.divisionStats!).filter(([d]) => d !== stats.division && d !== 'unassigned');
              if (otherDivs.length === 0) return null;
              return (
                <div className="spl-modal-extra">
                  <div className="spl-modal-extra-title">🏆 Points in Other Divisions</div>
                  <div className="spl-other-divs">
                    {otherDivs.map(([div, ds]) => (
                      <div key={div} className="spl-other-div">
                        <div className="pts acc">{ds.totalPoints}</div>
                        <div className="lbl">{div}</div>
                        <div className="sub">{ds.gamesPlayed} games</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Full match history */}
            {matchesByDate.length > 0 && (
              <div className="spl-modal-extra">
                <div className="spl-modal-extra-title">
                  📈 Match History <span className="spl-modal-extra-count">({history!.matches.length})</span>
                </div>
                {matchesByDate.map(({ key, matches }) => {
                  const wins = matches.filter(m => {
                    const ip1 = m.pair1.player1Id === stats.playerId || m.pair1.player2Id === stats.playerId;
                    return ip1 ? m.score.winnerPair === 1 : m.score.winnerPair === 2;
                  }).length;
                  const isOpen = expandedDates.has(key);
                  return (
                    <div key={key} className="spl-mh-group">
                      <button className="spl-mh-header" onClick={() => toggleDate(key)}>
                        <span className="date">{key}</span>
                        <span className="tally">
                          <span className="w">{wins}W</span>
                          <span className="sep">·</span>
                          <span className="l">{matches.length - wins}L</span>
                          <span className="total">({matches.length})</span>
                        </span>
                        <span className="arrow">{isOpen ? '↑' : '↓'}</span>
                      </button>
                      {isOpen && (
                        <div className="spl-mh-matches">
                          {matches.map((m, mi) => {
                            const ip1 = m.pair1.player1Id === stats.playerId || m.pair1.player2Id === stats.playerId;
                            const won = ip1 ? m.score.winnerPair === 1 : m.score.winnerPair === 2;
                            const partner = ip1
                              ? (m.pair1.player1Id === stats.playerId ? m.pair1Player2Name : m.pair1Player1Name)
                              : (m.pair2.player1Id === stats.playerId ? m.pair2Player2Name : m.pair2Player1Name);
                            const opp1 = ip1 ? m.pair2Player1Name : m.pair1Player1Name;
                            const opp2 = ip1 ? m.pair2Player2Name : m.pair1Player2Name;
                            const score = `${m.score.pair1Games}–${m.score.pair2Games}`;
                            return (
                              <div key={mi} className={`spl-mh-match ${won ? 'win' : 'loss'}`}>
                                <span className={`spl-res-badge ${won ? 'win' : 'loss'}`}>{won ? 'W' : 'L'}</span>
                                <div className="spl-mh-players">
                                  <div className="with">with <strong>{partner}</strong></div>
                                  <div className="vs">vs {opp1} / {opp2}</div>
                                </div>
                                <span className="spl-mh-score">{score}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────

type Mode = 'dark' | 'light';

function SplHeader({ lang, setLang, mode, setMode, t }: {
  lang: Lang; setLang: (l: Lang) => void;
  mode: Mode; setMode: (m: Mode) => void;
  t: typeof T['en'];
}) {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: 'smooth' });
  };
  return (
    <header className="spl-header">
      <div className="spl-c spl-header-inner">
        <a href="#top" className="spl-logo" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          <span className="mark">SP<em>L</em></span>
          <span className="sub">Stuttgart Padel League</span>
        </a>
        <nav className="spl-nav">
          {(['rankings', 'divisions', 'matches', 'about', 'join'] as const).map(key => (
            <a key={key} href={`#${key}`} onClick={e => { e.preventDefault(); scrollTo(key); }}>{t.nav[key]}</a>
          ))}
        </nav>
        <div className="spl-header-right">
          <button
            className="spl-mode-toggle"
            onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle light/dark mode"
          >
            {mode === 'dark' ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          <div className="spl-lang-toggle">
            <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
            <button className={lang === 'de' ? 'active' : ''} onClick={() => setLang('de')}>DE</button>
          </div>
        </div>
      </div>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────

function HeroSection({
  t, lang, seasonName, totalPlayers, totalMatches, onScrollTo,
}: {
  t: typeof T['en']; lang: Lang; seasonName: string; totalPlayers: number; totalMatches: number; onScrollTo: (id: string) => void;
}) {
  const stats = [
    { k: totalPlayers > 0 ? String(totalPlayers) : '—', v: t.heroStats[0].v },
    { k: t.heroStats[1].k!, v: t.heroStats[1].v },
    { k: totalMatches > 0 ? totalMatches.toLocaleString(lang === 'de' ? 'de-DE' : 'en-GB') : '—', v: t.heroStats[2].v },
    { k: t.heroStats[3].k!, v: t.heroStats[3].v },
  ];
  return (
    <section className="spl-hero" id="top">
      <div className="spl-c">
        <div className="spl-hero-grid">
          <div>
            <div className="spl-hero-eyebrow">
              <span className="spl-pulse" />
              <span className="spl-eyebrow">
                {t.season} — {seasonName || '…'}
              </span>
            </div>
            <h1 className="spl-hero-title">
              <span className="line">{t.hero.title_a}</span>
              <span className="line b">{t.hero.title_b}</span>
              <span className="line c">{t.hero.title_c} <em>est. 2019</em></span>
            </h1>
            <p className="spl-hero-lede">{t.hero.lede}</p>
            <div className="spl-hero-ctas">
              <button className="spl-btn spl-btn-primary" onClick={() => onScrollTo('rankings')}>
                {t.hero.cta1}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
              <button className="spl-btn spl-btn-ghost" onClick={() => onScrollTo('join')}>
                {t.hero.cta2}
              </button>
            </div>
          </div>

          <div className="spl-hero-court">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="spl-eyebrow">Master Division</div>
                <div className="spl-mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{seasonName || '…'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'var(--display)', fontSize: 44, fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>01</div>
                <div className="spl-eyebrow">Ranking</div>
              </div>
            </div>
            <CourtSvg />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <div>
                <div className="spl-eyebrow">Leader</div>
                <div style={{ fontWeight: 600, marginTop: 4, fontSize: 13 }}>Master Division</div>
              </div>
              <div className="spl-mono" style={{ fontSize: 11, color: 'var(--accent)' }}>— pts</div>
            </div>
          </div>
        </div>

        <div className="spl-hero-stats">
          {stats.map((s, i) => (
            <div key={i} className="spl-hero-stat">
              <div className="k">{s.k}<span className="dot">.</span></div>
              <div className="v">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Rankings Section ─────────────────────────────────────────────────────

interface RankingsSectionProps {
  t: typeof T['en'];
  lang: Lang;
  allSeasons: any[];
  selectedSeasonId: string | null;
  onSeasonChange: (id: string) => void;
  onPlayerClick: (p: PlayerStats, rank: number, divLabel: string, seasonName: string) => void;
}

function RankingsSection({ t, lang, allSeasons, selectedSeasonId, onSeasonChange, onPlayerClick }: RankingsSectionProps) {
  const [activeDiv, setActiveDiv] = useState(0);
  const [rankingsCache, setRankingsCache] = useState<Record<string, PlayerStats[]>>({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [hideGuests, setHideGuests] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showAll, setShowAll] = useState(false);

  const div = SPL_DIVISIONS[activeDiv];
  const cacheKey = `${div.id}::${selectedSeasonId ?? 'all'}`;

  useEffect(() => {
    if (rankingsCache[cacheKey]) return;
    setLoading(true);
    const params = new URLSearchParams({
      division: div.id,
      rankBy: 'hybrid',
      participationWeight: '40',
    });
    if (selectedSeasonId) params.append('seasonId', selectedSeasonId);
    apiRequest(`/api/public/rankings?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then((data: PlayerStats[]) => {
        const filtered = Array.isArray(data) ? data.filter(p => {
          const n = (p.playerName || '').toLowerCase();
          return !n.includes('test') && !n.includes('player_');
        }) : [];
        setRankingsCache(prev => ({ ...prev, [cacheKey]: filtered }));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [div.id, selectedSeasonId, cacheKey]);

  const raw = rankingsCache[cacheKey] || [];
  const filtered = useMemo(() => {
    let data = hideGuests ? raw.filter(p => !p.isGuest && !p.isGuestPlayer) : raw;
    const s = search.toLowerCase();
    return s ? data.filter(p => p.playerName.toLowerCase().includes(s)) : data;
  }, [raw, search, hideGuests]);

  const sorted = useMemo(() => {
    const data = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      switch (sortKey) {
        case 'points': return dir * (b.totalPoints - a.totalPoints);
        case 'played': return dir * ((b.matchesWon + b.matchesLost) - (a.matchesWon + a.matchesLost));
        case 'wins':   return dir * (b.matchesWon - a.matchesWon);
        case 'losses': return dir * (b.matchesLost - a.matchesLost);
        default:       return 0; // rank is natural order
      }
    });
    return data;
  }, [filtered, sortKey, sortDir]);

  const displayed = showAll ? sorted : sorted.slice(0, 10);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const si = (key: SortKey) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? '↑' : '↓';
  };

  const selectedSeason = allSeasons.find(s => s.id === selectedSeasonId);
  const seasonName = selectedSeason?.name ?? '';

  return (
    <section className="spl-section" id="rankings">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">01 / Rankings</div>
            <h2 className="spl-section-title">{t.rankings.title}</h2>
          </div>
          <p className="spl-section-sub">{t.rankings.sub}</p>
        </div>

        {/* Division tabs */}
        <div className="spl-div-tabs">
          {SPL_DIVISIONS.map((d, i) => (
            <button key={d.id} className={`spl-div-tab${activeDiv === i ? ' active' : ''}`}
              onClick={() => { setActiveDiv(i); setSearch(''); setShowAll(false); }}>
              <span className="ti">{d.tier}</span>
              {d.shortLabel}
            </button>
          ))}
        </div>

        {/* Tools row */}
        <div className="spl-rankings-tools">
          <div className="spl-search">
            <span className="spl-search-icon">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.rankings.search} />
          </div>
          <select className="spl-season-sel" value={selectedSeasonId ?? ''} onChange={e => onSeasonChange(e.target.value)}>
            {allSeasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label className="spl-guest-toggle">
            <input type="checkbox" checked={hideGuests} onChange={e => setHideGuests(e.target.checked)} />
            <span className="spl-toggle-track"><span className="spl-toggle-thumb" /></span>
            <span className="spl-toggle-label">{t.rankings.hideGuests}</span>
          </label>
        </div>

        {/* Table */}
        <div className="spl-table-wrap">
          {loading ? (
            <div className="spl-spinner-wrap"><div className="spl-spinner" /><span className="spl-spinner-text">{t.loading}</span></div>
          ) : sorted.length === 0 ? (
            <div className="spl-empty">{t.rankings.empty}</div>
          ) : (
            <table className="spl-table">
              <thead>
                <tr>
                  <th className={sortKey === 'rank' ? 'sorted' : ''} onClick={() => handleSort('rank')}>
                    {t.rankings.h.rank}<span className="si">{si('rank')}</span>
                  </th>
                  <th>{t.rankings.h.player}</th>
                  <th className={`col-played${sortKey === 'played' ? ' sorted' : ''}`} onClick={() => handleSort('played')}>
                    {t.rankings.h.played}<span className="si">{si('played')}</span>
                  </th>
                  <th className={sortKey === 'wins' ? 'sorted' : ''} onClick={() => handleSort('wins')}>
                    {t.rankings.h.wins}<span className="si">{si('wins')}</span>
                  </th>
                  <th className={sortKey === 'losses' ? 'sorted' : ''} onClick={() => handleSort('losses')}>
                    {t.rankings.h.losses}<span className="si">{si('losses')}</span>
                  </th>
                  <th className={sortKey === 'points' ? 'sorted' : ''} onClick={() => handleSort('points')}>
                    {t.rankings.h.points}<span className="si">{si('points')}</span>
                  </th>
                  <th className="col-form">{t.rankings.h.form}</th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((p, i) => {
                  const rank = sorted.indexOf(p) + 1;
                  const isTop = rank <= 3;
                  const chg = p.rankChange;
                  const form = getFormBars(p.matchesWon, p.matchesLost);
                  return (
                    <tr key={p.playerId} onClick={() => onPlayerClick(p, rank, div.label, seasonName)}>
                      <td>
                        <div className={`rank-cell${isTop ? ' top' : ''}`}>
                          {String(rank).padStart(2, '0')}
                        </div>
                        {chg !== undefined && chg !== null && (
                          <span className={`rank-chg ${chg > 0 ? 'up' : chg < 0 ? 'dn' : ''}`}>
                            {chg > 0 ? `▲${chg}` : chg < 0 ? `▼${Math.abs(chg)}` : '·'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="player-cell">
                          <div className="spl-av">
                            {p.profilePicture
                              ? <img src={p.profilePicture} alt={p.playerName} />
                              : getInitials(p.playerName)}
                          </div>
                          <div>
                            <div className="player-name">{p.playerName}</div>
                            {p.nationality && <div className="player-nat">{p.nationality}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="col-played spl-num">{p.matchesWon + p.matchesLost}</td>
                      <td className="spl-num">{p.matchesWon}</td>
                      <td className="spl-num">{p.matchesLost}</td>
                      <td className="spl-pts">{p.totalPoints}</td>
                      <td className="col-form">
                        <div className="form-bars">
                          {form.map((f, fi) => <div key={fi} className={`fb ${f}`} />)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!loading && sorted.length > 10 && (
          <div className="spl-view-all">
            <button onClick={() => setShowAll(v => !v)}>
              {showAll ? '← Show less' : t.rankings.viewAll}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Divisions Section ────────────────────────────────────────────────────

function DivisionsSection({
  t, lang, onDivisionClick, rankingsByDiv,
}: {
  t: typeof T['en']; lang: Lang;
  onDivisionClick: (divIndex: number) => void;
  rankingsByDiv: Record<string, PlayerStats[]>;
}) {
  return (
    <section className="spl-section spl-section-alt" id="divisions">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">02 / Structure</div>
            <h2 className="spl-section-title">{t.divisions.title}</h2>
          </div>
          <p className="spl-section-sub">{t.divisions.sub}</p>
        </div>
        <div className="spl-div-grid">
          {SPL_DIVISIONS.map((d, i) => {
            const players = rankingsByDiv[d.id] || [];
            const leader = players[0];
            return (
              <div key={d.id} className="spl-div-card" onClick={() => onDivisionClick(i)}>
                <span className={`spl-div-badge ${d.kind}`}>
                  {d.kind === 'women' ? t.divisions.women : t.divisions.open}
                </span>
                <div className="spl-div-tier">{lang === 'de' ? 'Stufe' : 'Tier'} {d.tier}</div>
                <div className={`spl-div-name${d.id === 'Master' ? ' master' : ''}`}>{d.shortLabel}</div>
                <div className="spl-div-count">
                  {players.length > 0 ? `${players.length} ${t.divisions.players}` : '—'}
                </div>
                {leader && (
                  <div className="spl-div-leader">
                    <div className="mini-av">{getInitials(leader.playerName)}</div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div className="leader-label">Leader</div>
                      <div className="leader-name">{leader.playerName}</div>
                    </div>
                    <span className="leader-pts">{leader.totalPoints} pts</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Matches Section ──────────────────────────────────────────────────────

function MatchesSection({ t, lang, leagueDays }: { t: typeof T['en']; lang: Lang; leagueDays: LeagueDay[] }) {
  const upcoming = useMemo(() => {
    const now = new Date();
    return leagueDays
      .filter(ld => !ld.isCompleted && !ld.isDraft)
      .sort((a, b) => toJsDate(a.date).getTime() - toJsDate(b.date).getTime())
      .slice(0, 6);
  }, [leagueDays]);

  if (upcoming.length === 0) return null;

  const divLabel = (d: Division) => SPL_DIVISIONS.find(x => x.id === d)?.shortLabel ?? d;

  return (
    <section className="spl-section" id="matches">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">03 / Matches</div>
            <h2 className="spl-section-title">{t.matches.title}</h2>
          </div>
          <p className="spl-section-sub">{t.matches.sub}</p>
        </div>
        <div className="spl-matches-grid">
          {upcoming.map(ld => {
            const status = getLeagueDayStatus(ld);
            const date = toJsDate(ld.date);
            return (
              <div key={ld.id} className="spl-match-card">
                <div className="spl-match-top">
                  <span className={`spl-status-pill ${status}`}>
                    {status === 'live' && <span className="status-dot" />}
                    {t.matches[status]}
                  </span>
                  <span className="spl-match-div-label">{divLabel(ld.division)}</span>
                </div>
                <div>
                  <div className="spl-match-day-name">{ld.name || divLabel(ld.division)}</div>
                  {ld.selectedPlayers?.length > 0 && (
                    <div className="spl-match-meta" style={{ marginTop: 4 }}>
                      {ld.selectedPlayers.length} {lang === 'de' ? 'Spieler' : 'players'} · Round {ld.currentRound}
                    </div>
                  )}
                </div>
                <div className="spl-match-footer">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {fmtDate(date, lang)}
                  {ld.time && ` · ${ld.time}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Season Snapshot ──────────────────────────────────────────────────────

function StandingsSection({
  t, lang, rankingsByDiv, onPlayerClick, selectedSeasonId, allSeasons,
}: {
  t: typeof T['en']; lang: Lang;
  rankingsByDiv: Record<string, PlayerStats[]>;
  onPlayerClick: (p: PlayerStats, rank: number, divLabel: string, seasonName: string) => void;
  selectedSeasonId: string | null;
  allSeasons: any[];
}) {
  const seasonName = allSeasons.find(s => s.id === selectedSeasonId)?.name ?? '';
  const divs = SNAPSHOT_DIVS.filter(d => (rankingsByDiv[d]?.length ?? 0) > 0);
  if (divs.length === 0) return null;

  return (
    <section className="spl-section spl-section-alt" id="standings">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">04 / Standings</div>
            <h2 className="spl-section-title">{t.standings.title}</h2>
          </div>
          <p className="spl-section-sub">{t.standings.sub}</p>
        </div>
        <div className="spl-snap-grid">
          {divs.map(divId => {
            const players = (rankingsByDiv[divId] || []).slice(0, 6);
            const divCfg = SPL_DIVISIONS.find(d => d.id === divId);
            const gap = players.length >= 2 ? players[0].totalPoints - players[1].totalPoints : 0;
            const promotionLine = Math.min(2, players.length - 1);
            const relegationLine = Math.max(3, players.length - 3);
            return (
              <div key={divId} className="spl-snap-card">
                <div className="spl-snap-head">
                  <span className="spl-snap-div">{divCfg?.shortLabel}</span>
                  {gap > 0 && (
                    <span className="spl-snap-gap">
                      +{gap} {lang === 'de' ? 'Pkt.' : 'pts'} {lang === 'de' ? 'zu Platz 2' : 'to 2nd'}
                    </span>
                  )}
                </div>
                {players.map((p, i) => {
                  const isPromotion = i < 2;
                  const isRelegation = i >= relegationLine;
                  const isPromoLine = i === promotionLine - 1;
                  return (
                    <div key={p.playerId}
                      className={`spl-snap-row${isPromoLine ? ' promo-line' : ''}`}
                      onClick={() => onPlayerClick(p, i + 1, divCfg?.label ?? divId, seasonName)}>
                      <div className={`spl-snap-tick ${isPromotion ? 'up' : isRelegation ? 'dn' : ''}`} />
                      <span className="spl-snap-rank">#{i + 1}</span>
                      <span className="spl-snap-name">{p.playerName}</span>
                      <span className="spl-snap-pts">{p.totalPoints}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Recent Results ───────────────────────────────────────────────────────

interface ResultEntry {
  date: Date;
  winner1: string;
  winner2: string;
  loser1: string;
  loser2: string;
  score: string;
  division: string;
}

function ResultsSection({ t, lang, results }: { t: typeof T['en']; lang: Lang; results: ResultEntry[] }) {
  if (results.length === 0) return null;
  return (
    <section className="spl-section" id="results">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">05 / Results</div>
            <h2 className="spl-section-title">{t.results.title}</h2>
          </div>
          <p className="spl-section-sub">{t.results.sub}</p>
        </div>
        <div className="spl-results-list">
          {results.map((r, i) => (
            <div key={i} className="spl-result-row">
              <span className="spl-result-date">{fmtShortDate(r.date, lang)}</span>
              <span className="spl-result-winner">{r.winner1} / {r.winner2}</span>
              <span className="spl-result-def">{t.results.def}</span>
              <span className="spl-result-loser">{r.loser1} / {r.loser2}</span>
              <span className="spl-result-score">{r.score}</span>
              <span className="spl-result-division">{r.division}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── News Section ─────────────────────────────────────────────────────────

function NewsSection({ t }: { t: typeof T['en'] }) {
  return (
    <section className="spl-section spl-section-alt" id="news">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">06 / News</div>
            <h2 className="spl-section-title">{t.news.title}</h2>
          </div>
          <p className="spl-section-sub">{t.news.sub}</p>
        </div>
        <div className="spl-news-grid">
          {PLACEHOLDER_NEWS.map((n, i) => (
            <div key={i} className="spl-news-card">
              <div className="spl-news-img">
                <NewsPlaceholderSvg />
                <span className="spl-news-placeholder-label">League photography</span>
              </div>
              <div className="spl-news-body">
                <div className="spl-news-meta">
                  <span className="spl-news-tag">{n.tag}</span>
                  <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 9 }}>·</span>
                  <span className="spl-news-date">{n.date}</span>
                </div>
                <div className="spl-news-title">{n.title}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── About Section ────────────────────────────────────────────────────────

function AboutSection({ t, totalPlayers }: { t: typeof T['en']; totalPlayers: number }) {
  const facts = [
    { k: '2019', v: t.about.facts[0].v },
    { k: totalPlayers > 0 ? String(totalPlayers) : t.about.facts[1].k, v: t.about.facts[1].v },
    { k: t.about.facts[2].k, v: t.about.facts[2].v },
    { k: t.about.facts[3].k, v: t.about.facts[3].v },
  ];
  return (
    <section className="spl-section" id="about">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">07 / About</div>
          </div>
        </div>
        <div className="spl-about-grid">
          <div>
            <h2 className="spl-section-title" style={{ marginBottom: 24 }}>{t.about.title}</h2>
            <p className="spl-about-lede">{t.about.lede}</p>
            <div className="spl-facts-grid">
              {facts.map((f, i) => (
                <div key={i} className="spl-fact">
                  <div className="k">{f.k}<span style={{ color: 'var(--accent)' }}>.</span></div>
                  <div className="v">{f.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="spl-about-img-col">
            <NewsPlaceholderSvg />
            <span className="spl-news-placeholder-label" style={{ position: 'relative', zIndex: 1 }}>League photography</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Join Section ─────────────────────────────────────────────────────────

function JoinSection({ t, lang }: { t: typeof T['en']; lang: Lang }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
  };

  return (
    <section className="spl-section spl-section-alt" id="join">
      <div className="spl-c">
        <div className="spl-join-grid">
          <div>
            <h2 className="spl-join-title">
              {t.join.title_a} <span className="accent">{t.join.title_b}</span>
            </h2>
            <p className="spl-join-lede">{t.join.lede}</p>
            <div className="spl-steps">
              {[t.join.step1, t.join.step2, t.join.step3].map((step, i) => (
                <div key={i} className="spl-step">
                  <span className="spl-step-num">0{i + 1}</span>
                  <span className="spl-step-text">{step}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="spl-email-card">
              <h3>{lang === 'de' ? 'Dabei sein' : 'Get involved'}</h3>
              {submitted ? (
                <div className="spl-email-success">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {lang === 'de' ? 'Danke. Wir melden uns.' : "Thanks. We'll be in touch."}
                </div>
              ) : (
                <form className="spl-email-form" onSubmit={handleSubmit}>
                  <input
                    className="spl-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t.join.placeholder}
                  />
                  <button className="spl-btn spl-btn-primary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>
                    {t.join.cta}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Sponsors Section ─────────────────────────────────────────────────────

function SponsorsSection({ t }: { t: typeof T['en'] }) {
  return (
    <section className="spl-section" id="sponsors" style={{ padding: '64px 0' }}>
      <div className="spl-c">
        <div style={{ marginBottom: 28 }}>
          <span className="spl-eyebrow">{t.sponsors.title}</span>
        </div>
        <div className="spl-sponsors-row">
          {PLACEHOLDER_SPONSORS.map((s, i) => (
            <div key={i} className="spl-sponsor">
              <div className="spl-sponsor-name">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ Section ──────────────────────────────────────────────────────────

function FaqSection({ t }: { t: typeof T['en'] }) {
  const [openIdx, setOpenIdx] = useState<number>(0);
  return (
    <section className="spl-section spl-section-alt" id="faq">
      <div className="spl-c">
        <div className="spl-section-head">
          <div>
            <div className="spl-section-index">08 / FAQ</div>
            <h2 className="spl-section-title">{t.faq.title}</h2>
          </div>
        </div>
        <div className="spl-faq-list">
          {t.faq.items.map((item, i) => (
            <div key={i} className={`spl-faq-item${openIdx === i ? ' open' : ''}`}>
              <button className="spl-faq-trigger" onClick={() => setOpenIdx(openIdx === i ? -1 : i)}>
                <span className="spl-faq-q">{item.q}</span>
                <span className="spl-faq-icon">+</span>
              </button>
              <div className="spl-faq-answer">
                <p>{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────

function SplFooter({ t }: { t: typeof T['en'] }) {
  const linkHrefs = ['/impressum', '/privacy', '#'];
  return (
    <footer className="spl-footer">
      <div className="spl-c">
        <div className="spl-footer-main">
          <div>
            <div className="spl-footer-mark">SP<em>L</em></div>
            <div className="spl-footer-tagline">{t.footer.tagline}</div>
          </div>
          <div className="spl-footer-links">
            {t.footer.links.map((l, i) => <a key={i} href={linkHrefs[i] ?? '#'}>{l}</a>)}
          </div>
        </div>
        <div className="spl-footer-bottom">
          <span className="spl-copyright">{t.footer.rights}</span>
          <span className="spl-coords">STUTTGART · 48.7758° N, 9.1829° E</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────

interface SplLandingProps {
  initialMode?: Mode;
}

export default function SplLanding({ initialMode = 'dark' }: SplLandingProps) {
  const { activeSeason, allSeasons, isLoading: seasonLoading } = useSeason();

  const [lang, setLang] = useState<Lang>('en');
  const [mode, setMode] = useState<Mode>(initialMode);
  const [modalPlayer, setModalPlayer] = useState<ModalPlayer | null>(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);

  // Data
  const [allLeagueDays, setAllLeagueDays] = useState<LeagueDay[]>([]);
  const [rankingsByDiv, setRankingsByDiv] = useState<Record<string, PlayerStats[]>>({});
  const [recentResults, setRecentResults] = useState<ResultEntry[]>([]);

  // Tab state for divisions — lifted so DivisionsSection can navigate Rankings
  const [rankingsTabRef, setRankingsTabRef] = useState<{ setActiveDiv: (i: number) => void } | null>(null);

  const t = T[lang];

  // Set season once loaded
  useEffect(() => {
    if (!selectedSeasonId && activeSeason) setSelectedSeasonId(activeSeason.id);
  }, [activeSeason, selectedSeasonId]);

  // Fetch league days for all SPL divisions for the matches section
  useEffect(() => {
    if (!selectedSeasonId) return;
    const fetchAll = async () => {
      const results: LeagueDay[] = [];
      await Promise.all(SPL_DIVISIONS.map(async d => {
        try {
          const r = await apiRequest(`/api/public/league-days?division=${encodeURIComponent(d.id)}&seasonId=${selectedSeasonId}`);
          if (r.ok) {
            const data = await r.json();
            if (Array.isArray(data)) results.push(...data);
          }
        } catch { /* silent */ }
      }));
      setAllLeagueDays(results);
    };
    fetchAll();
  }, [selectedSeasonId]);

  // Fetch rankings for key divisions (snapshot + divisions section)
  useEffect(() => {
    if (!selectedSeasonId) return;
    const keyDivs: Division[] = ['Master', 'Division 1', 'Division 2A', 'Division 2B', 'Division 3', 'Division 4', 'Women Division', 'Women Division 2'];
    const fetchDiv = async (div: Division) => {
      const params = new URLSearchParams({
        division: div, rankBy: 'hybrid', participationWeight: '40', seasonId: selectedSeasonId,
      });
      try {
        const r = await apiRequest(`/api/public/rankings?${params}`);
        if (r.ok) {
          const data: PlayerStats[] = await r.json();
          if (Array.isArray(data)) {
            const filtered = data.filter(p => {
              const n = (p.playerName || '').toLowerCase();
              return !n.includes('test') && !n.includes('player_');
            });
            setRankingsByDiv(prev => ({ ...prev, [div]: filtered }));
          }
        }
      } catch { /* silent */ }
    };
    keyDivs.forEach(fetchDiv);
  }, [selectedSeasonId]);

  // Derive recent results from completed league days + their match data
  useEffect(() => {
    const completed = allLeagueDays
      .filter(ld => ld.isCompleted)
      .sort((a, b) => toJsDate(b.date).getTime() - toJsDate(a.date).getTime())
      .slice(0, 2);

    if (completed.length === 0) return;

    const fetchResults = async () => {
      const entries: ResultEntry[] = [];
      for (const ld of completed) {
        try {
          const r = await apiRequest(`/api/public/league-days/${ld.id}/matches?division=${encodeURIComponent(ld.division)}`);
          if (!r.ok) continue;
          const data = await r.json();
          const matches = data.matches ?? [];
          for (const m of matches.slice(0, 4)) {
            if (!m.isCompleted || m.score.winnerPair === null) continue;
            const isP1Winner = m.score.winnerPair === 1;
            const divCfg = SPL_DIVISIONS.find(d => d.id === ld.division);
            entries.push({
              date: toJsDate(ld.date),
              winner1: isP1Winner ? m.pair1Player1Name : m.pair2Player1Name,
              winner2: isP1Winner ? m.pair1Player2Name : m.pair2Player2Name,
              loser1: isP1Winner ? m.pair2Player1Name : m.pair1Player1Name,
              loser2: isP1Winner ? m.pair2Player2Name : m.pair1Player2Name,
              score: `${Math.max(m.score.pair1Games, m.score.pair2Games)}—${Math.min(m.score.pair1Games, m.score.pair2Games)}`,
              division: divCfg?.shortLabel ?? ld.division,
            });
          }
        } catch { /* silent */ }
      }
      setRecentResults(entries);
    };
    fetchResults();
  }, [allLeagueDays]);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
  }, []);

  const totalPlayers = useMemo(() => {
    const seen = new Set<string>();
    Object.values(rankingsByDiv).forEach(players =>
      players.forEach(p => seen.add(p.playerId))
    );
    return seen.size;
  }, [rankingsByDiv]);

  const totalMatchesSeason = useMemo(() => {
    return Object.values(rankingsByDiv).reduce((acc, players) =>
      acc + players.reduce((s, p) => s + p.matchesWon + p.matchesLost, 0), 0
    );
  }, [rankingsByDiv]);

  const handlePlayerClick = useCallback((p: PlayerStats, rank: number, divLabel: string, seasonName: string) => {
    setModalPlayer({ stats: p, rank, divLabel, seasonName });
  }, []);

  const handleDivisionCardClick = useCallback((divIndex: number) => {
    scrollTo('rankings');
    // Rankings section manages its own state, we just scroll there
  }, [scrollTo]);

  if (seasonLoading && allSeasons.length === 0) {
    return (
      <div className="spl" data-mode={mode} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spl-spinner-wrap">
          <div className="spl-spinner" />
          <span className="spl-spinner-text">LOADING</span>
        </div>
      </div>
    );
  }

  return (
    <div className="spl" data-mode={mode}>
      <SplHeader lang={lang} setLang={setLang} mode={mode} setMode={setMode} t={t} />

      <HeroSection
        t={t} lang={lang}
        seasonName={allSeasons.find(s => s.id === selectedSeasonId)?.name ?? activeSeason?.name ?? '…'}
        totalPlayers={totalPlayers}
        totalMatches={totalMatchesSeason}
        onScrollTo={scrollTo}
      />

      <RankingsSection
        t={t} lang={lang}
        allSeasons={allSeasons}
        selectedSeasonId={selectedSeasonId}
        onSeasonChange={id => setSelectedSeasonId(id)}
        onPlayerClick={handlePlayerClick}
      />

      <DivisionsSection
        t={t} lang={lang}
        onDivisionClick={handleDivisionCardClick}
        rankingsByDiv={rankingsByDiv}
      />

      <MatchesSection t={t} lang={lang} leagueDays={allLeagueDays} />

      <StandingsSection
        t={t} lang={lang}
        rankingsByDiv={rankingsByDiv}
        onPlayerClick={handlePlayerClick}
        selectedSeasonId={selectedSeasonId}
        allSeasons={allSeasons}
      />

      <ResultsSection t={t} lang={lang} results={recentResults} />

      <NewsSection t={t} />

      <AboutSection t={t} totalPlayers={totalPlayers} />

      <JoinSection t={t} lang={lang} />

      <SponsorsSection t={t} />

      <FaqSection t={t} />

      <SplFooter t={t} />

      {modalPlayer && (
        <PlayerModal
          player={modalPlayer}
          onClose={() => setModalPlayer(null)}
          t={t}
          lang={lang}
          seasonId={selectedSeasonId}
        />
      )}
    </div>
  );
}
